<template>
  <div :class="layout.wrapper">
    <!-- La misma lista vertical siempre: columna lateral desde LAYOUT y, por debajo, a ancho completo encima del contenido -->
    <aside :class="layout.aside">
      <nav :aria-label="$t(ariaLabel)" :class="layout.nav">
        <ul class="flex flex-col gap-1">
          <li v-for="section in sections" :key="section.key">
            <router-link :to="section.to" custom v-slot="{ href, navigate, isExactActive }">
              <a :href="href" @click="select($event, navigate)"
                 :aria-current="ariaCurrent(section, isExactActive)"
                 :class="[isActive(section, isExactActive)
                   ? 'bg-white text-bpblue shadow-sm ring-1 ring-gray-200'
                   : 'text-gray-700 hover:bg-white/70 hover:text-gray-900', layout.item]"
                 class="group flex items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors duration-150 motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue">
                <font-awesome-icon v-if="section.icon" :icon="section.icon" aria-hidden="true"
                                   :class="isActive(section, isExactActive) ? 'text-bpblue' : 'text-gray-400 group-hover:text-gray-500'"
                                   class="h-5 w-5 flex-shrink-0 transition-colors duration-150 motion-reduce:transition-none"/>
                <span class="min-w-0">
                  <span class="block">{{ $t(section.label) }}</span>
                  <span v-if="section.description" class="line-clamp-2 text-xs font-normal text-gray-500">{{ $t(section.description) }}</span>
                </span>
              </a>
            </router-link>
          </li>
        </ul>
      </nav>
    </aside>

    <div ref="content" tabindex="-1" :class="layout.content" class="min-w-0 focus:outline-none">
      <slot></slot>
    </div>
  </div>
</template>

<script>
/*
 * Desde qué ancho la lista pasa a ser una columna al lado del contenido: xl
 * (1280px). Por debajo, el menú principal del panel (desde md) dejaba al
 * contenido sin sitio. Para cambiar el corte, se cambian aquí las clases y la
 * media query a la vez (p. ej. todo a lg: y 1024px). Las clases van escritas
 * enteras porque Tailwind solo genera las que encuentra literalmente.
 */
const LAYOUT = {
  wrapper: 'xl:grid xl:grid-cols-12 xl:gap-x-8',
  aside: 'xl:col-span-3',
  nav: 'xl:sticky xl:top-6',
  item: 'py-1.5 xl:py-2',
  content: 'mt-4 xl:col-span-9 xl:mt-0',
  // Mismo corte que las clases: el salto de foco solo cuando la lista va encima.
  sideBySideQuery: '(min-width: 1280px)',
}

/*
 * Navegación entre las secciones de una página (Personalización…): una lista
 * vertical de enlaces con título y, opcionalmente, una descripción corta (hasta
 * dos líneas; si alguna traducción es más larga, se corta con «…»).
 *
 * Cada sección: { key, to, label (clave i18n), description? (clave i18n),
 * icon? (nombre de Font Awesome), activeNames? (nombres de ruta) }.
 * Con activeNames la sección queda activa también en esas rutas (sus subpantallas):
 * router-link solo marca el enlace que coincide exactamente.
 */
export default {
  name: "SectionNavigationComponent",
  props: {
    sections: {
      type: Array,
      required: true
    },
    // Clave i18n del nombre del <nav> para lectores de pantalla.
    ariaLabel: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      layout: LAYOUT,
    }
  },
  methods: {
    isActive(section, isExactActive) {
      return isExactActive || (section.activeNames ?? []).includes(this.$route.name)
    },
    // "page" en la página exacta; "true" en una subpantalla de la sección (WAI-ARIA).
    ariaCurrent(section, isExactActive) {
      if (isExactActive) {
        return 'page'
      }

      return this.isActive(section, isExactActive) ? 'true' : null
    },
    /*
     * Cuando la lista va encima del contenido, al elegir una sección el contenido
     * cambia debajo, fuera de la vista: se lleva el foco (y el scroll) al contenido.
     * Solo al hacer clic, nunca al cargar la página. Con Ctrl/Cmd+clic el router no
     * navega ni marca el evento, y entonces no se mueve el foco.
     */
    async select(event, navigate) {
      const listAbove = !window.matchMedia(LAYOUT.sideBySideQuery).matches

      await navigate(event)

      if (listAbove && event.defaultPrevented) {
        await this.$nextTick()
        this.$refs.content?.focus()
      }
    }
  }
}
</script>
